// Máquina de estados do upload direto de vídeo.
//
// O caminho feliz é: mint -> envia blocos -> commit. Os dois caminhos que
// importam de verdade são os outros: retomar de onde parou (sem reenviar
// gigabytes) e cancelar sem deixar lixo no storage.

import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadBlocks } from '@/services/videoUpload/blockUploader';
import { videoUploadApi } from '@/services/videoUpload/api';
import type { AttachedVideo, UploadProtocol } from '@/services/videoUpload/types';
import { AbortedError } from '@/services/videoUpload/xhrPut';
import { UserFacingError } from '@/utils/videoUploadError';

export type UploadPhase = 'idle' | 'preparing' | 'uploading' | 'finalizing' | 'error';

/** O commit assíncrono é raro (a cópia estourou o timeout do servidor) mas
 *  precisa terminar: a task `finalize_video_uploads` assume e nós esperamos. */
const FINALIZE_POLL_MS = 2_000;
const FINALIZE_TIMEOUT_MS = 120_000;
/** Estados em que os blocos já subiram e só falta publicar. */
const POST_UPLOAD_STATUSES = new Set(['COMMITTED', 'COPYING']);

interface UseVideoUploadOptions {
  lessonId: string;
  onAttached: (video: AttachedVideo, lesson: unknown) => void;
  onError: (err: unknown, fileName: string) => void;
}

interface LiveSession {
  id: string;
  file: File;
  protocol: UploadProtocol;
  blockSizeBytes: number;
  blockCount: number;
  maxConcurrency: number;
}

export function useVideoUpload({ lessonId, onAttached, onError }: UseVideoUploadOptions) {
  const [phase, setPhase] = useState<UploadPhase>('idle');
  const [percent, setPercent] = useState(0);
  const [fileName, setFileName] = useState('');

  const sessionRef = useRef<LiveSession | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Sair da tela no meio do envio cancela os XHRs em voo. A sessão fica
      // órfã no banco e a varredura de inatividade a recolhe.
      abortRef.current?.abort();
    };
  }, []);

  const safeSet = useCallback(<T,>(setter: (v: T) => void, value: T) => {
    if (mountedRef.current) setter(value);
  }, []);

  /** A barra nunca anda para trás: uma retentativa zera o parcial do bloco,
   *  e um progresso que regride lê como "o envio perdeu trabalho" — não perdeu,
   *  os blocos já confirmados continuam lá. */
  const reportBytes = useCallback(
    (loaded: number, total: number) => {
      const next = total > 0 ? Math.min(99, Math.floor((loaded / total) * 100)) : 0;
      if (mountedRef.current) setPercent((prev) => Math.max(prev, next));
    },
    []
  );

  const waitForPublication = useCallback(
    async (sessionId: string): Promise<AttachedVideo> => {
      const deadline = Date.now() + FINALIZE_TIMEOUT_MS;
      for (;;) {
        await new Promise((r) => setTimeout(r, FINALIZE_POLL_MS));
        // Saiu da tela: para de sondar. O servidor termina a publicação
        // sozinho — insistir aqui só gastaria 2 minutos de requisições.
        if (!mountedRef.current) throw new AbortedError();
        const detail = await videoUploadApi.detail(lessonId, sessionId);
        if (detail.status === 'ATTACHED' && detail.video) return detail.video;
        if (detail.status === 'FAILED' || detail.status === 'REJECTED') {
          throw new UserFacingError(
            "O servidor recusou o arquivo ao publicar. Confira se o vídeo não está corrompido."
          );
        }
        if (Date.now() > deadline) {
          throw new UserFacingError(
            'O vídeo subiu, mas a publicação está demorando. Recarregue a página em alguns minutos.'
          );
        }
      }
    },
    [lessonId]
  );

  /** Compõe, valida e publica. Separado do envio porque a retomada de uma
   *  sessão cujos blocos JÁ subiram (o commit é que falhou) precisa disto
   *  sozinho — reenviar os blocos seria desperdício puro. */
  const finish = useCallback(
    async (session: LiveSession, title: string) => {
      safeSet(setPhase, 'finalizing');
      const result = await videoUploadApi.commit(lessonId, session.id, title);
      const video = result.finalizing ? await waitForPublication(session.id) : result.video!;

      sessionRef.current = null;
      abortRef.current = null;
      if (mountedRef.current) {
        setPercent(100);
        setPhase('idle');
      }
      onAttached(video, result.lesson);
    },
    [lessonId, onAttached, safeSet, waitForPublication]
  );

  const runUpload = useCallback(
    async (session: LiveSession, grant: Parameters<typeof uploadBlocks>[0]['grant'], staged: number[], title: string) => {
      const controller = new AbortController();
      abortRef.current = controller;
      safeSet(setPhase, 'uploading');

      await uploadBlocks({
        file: session.file,
        protocol: session.protocol,
        blockSizeBytes: session.blockSizeBytes,
        blockCount: session.blockCount,
        maxConcurrency: session.maxConcurrency,
        grant,
        stagedIndexes: staged,
        renewSas: () => videoUploadApi.renew(lessonId, session.id),
        onProgress: (loaded) => reportBytes(loaded, session.file.size),
        signal: controller.signal,
      });

      await finish(session, title);
    },
    [finish, lessonId, reportBytes, safeSet]
  );

  const start = useCallback(
    async (file: File, title = '') => {
      setFileName(file.name);
      setPercent(0);
      safeSet(setPhase, 'preparing');
      try {
        const created = await videoUploadApi.create(lessonId, file);
        const session: LiveSession = {
          id: created.session_id,
          file,
          protocol: created.protocol,
          blockSizeBytes: created.block_size_bytes,
          blockCount: created.block_count,
          maxConcurrency: created.max_concurrency,
        };
        sessionRef.current = session;
        await runUpload(session, created, [], title);
      } catch (err) {
        if (err instanceof AbortedError) return; // cancelamento não é falha
        safeSet(setPhase, 'error');
        onError(err, file.name);
      }
    },
    [lessonId, onError, runUpload, safeSet]
  );

  /** Retoma a MESMA sessão: pergunta ao servidor quais blocos já chegaram e
   *  reenvia só o que falta. É o que evita reenviar 2 GiB por causa de um
   *  túnel que caiu aos 90%. */
  const retry = useCallback(
    async (title = '') => {
      const session = sessionRef.current;
      if (!session) return;
      safeSet(setPhase, 'preparing');
      try {
        const detail = await videoUploadApi.detail(lessonId, session.id);
        if (detail.status === 'ATTACHED' && detail.video) {
          sessionRef.current = null;
          safeSet(setPhase, 'idle');
          onAttached(detail.video, null);
          return;
        }
        if (!detail.upload_url || detail.sas_ttl_seconds === null) {
          // Sessão que já saiu da fase de envio (o commit é que falhou):
          // os blocos estão lá, basta mandar publicar de novo.
          if (POST_UPLOAD_STATUSES.has(detail.status)) {
            await finish(session, title);
            return;
          }
          sessionRef.current = null; // retomar não vai adiantar
          throw new UserFacingError('Esta sessão não aceita mais envio. Selecione o arquivo de novo.');
        }
        await runUpload(
          session,
          {
            upload_url: detail.upload_url,
            sas_ttl_seconds: detail.sas_ttl_seconds,
            expires_at: detail.expires_at ?? '',
          },
          detail.staged_block_indexes ?? [],
          title
        );
      } catch (err) {
        if (err instanceof AbortedError) return;
        safeSet(setPhase, 'error');
        onError(err, session.file.name);
      }
    },
    [finish, lessonId, onError, onAttached, runUpload, safeSet]
  );

  const cancel = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = null;
    const session = sessionRef.current;
    sessionRef.current = null;
    if (mountedRef.current) {
      setPhase('idle');
      setPercent(0);
    }
    // Best-effort: se o DELETE falhar, a varredura de inatividade recolhe.
    if (session) await videoUploadApi.cancel(lessonId, session.id).catch(() => {});
  }, [lessonId]);

  return {
    phase,
    percent,
    fileName,
    /** Só existe sessão retomável depois de uma falha no meio do envio. */
    canRetry: phase === 'error' && sessionRef.current !== null,
    start,
    retry,
    cancel,
  };
}
